import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Clock, ChevronRight, Loader2, ArrowRight, AlertTriangle, CheckCircle2, BookMarked } from 'lucide-react';
import { BlogLayout } from '../components/BlogLayout';
import { BlogSeo } from '../components/BlogSeo';
import { NewsletterSignup } from '../components/NewsletterSignup';

const API = '/api';
const SITE = 'https://hippocrate.mg';

const renderContent = (text) => {
  if (!text) return null;
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        if (line.startsWith('•')) {
          return (
            <div key={i} className="flex gap-2.5 text-[15px] leading-[1.65] text-slate-200">
              <span className="text-indigo-400 mt-0.5 shrink-0">•</span>
              <span>{line.replace(/^•\s*/, '')}</span>
            </div>
          );
        }
        if (line.startsWith('-')) {
          return (
            <div key={i} className="flex gap-2.5 text-[14px] leading-[1.6] text-slate-300 pl-5">
              <span className="text-slate-500 mt-0.5 shrink-0">–</span>
              <span>{line.replace(/^-\s*/, '')}</span>
            </div>
          );
        }
        return (
          <p key={i} className="text-[15px] leading-[1.65] text-slate-200">{line}</p>
        );
      })}
    </div>
  );
};

export const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios
      .get(`${API}/blog/articles/${slug}`)
      .then((res) => {
        if (!active) return;
        setArticle(res.data.article);
        setRelated(res.data.related || []);
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <BlogLayout>
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
        </div>
      </BlogLayout>
    );
  }

  if (notFound || !article) {
    return (
      <BlogLayout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="blog-not-found">
          <h1 className="text-2xl font-bold text-white mb-3">Fiche introuvable</h1>
          <p className="text-slate-400 mb-6">Cette fiche documentaire n'existe pas ou a été retirée.</p>
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300">
            <ChevronRight className="w-4 h-4 rotate-180" /> Retour au blog
          </Link>
        </div>
      </BlogLayout>
    );
  }

  const url = `${SITE}/blog/${article.slug}`;
  const faqValid = (article.faq || []).filter((f) => f.question && f.answer);
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      headline: article.title,
      description: article.meta_description || article.excerpt,
      inLanguage: 'fr',
      datePublished: article.created_at,
      dateModified: article.updated_at,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      author: { '@type': 'Organization', name: 'Hippocrate MG' },
      publisher: {
        '@type': 'Organization',
        name: 'Hippocrate MG',
        logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` },
      },
      audience: { '@type': 'MedicalAudience', audienceType: 'Professionnels de santé' },
      about: article.category_label,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Blog', item: `${SITE}/blog` },
        { '@type': 'ListItem', position: 2, name: article.category_label, item: `${SITE}/blog/categorie/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: url },
      ],
    },
  ];
  if (faqValid.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqValid.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <BlogLayout>
      <BlogSeo
        title={`${article.title} | Hippocrate MG`}
        description={article.meta_description || article.excerpt}
        path={`/blog/${article.slug}`}
        image={`${SITE}/og-image.jpg`}
        jsonLd={jsonLd}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Fil d'Ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6 flex-wrap" data-testid="blog-breadcrumb">
          <Link to="/blog" className="hover:text-indigo-300">Blog</Link>
          <ChevronRight className="w-3 h-3 text-slate-600" />
          <Link to={`/blog/categorie/${article.category}`} className="hover:text-indigo-300">{article.category_label}</Link>
        </nav>

        {/* En-tête */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-3 text-xs mb-4">
            <span className="rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2.5 py-0.5">
              {article.category_label}
            </span>
            <span className="inline-flex items-center gap-1 text-slate-500">
              <Clock className="w-3 h-3" /> {article.reading_minutes} min de lecture
            </span>
            {article.created_at && <span className="text-slate-500">· {fmtDate(article.created_at)}</span>}
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight"
            style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}
            data-testid="blog-article-title"
          >
            {article.title}
          </h1>
          {article.excerpt && <p className="mt-4 text-lg text-slate-300 leading-relaxed">{article.excerpt}</p>}
          <p className="mt-3 text-xs text-slate-500">Par {article.author}</p>
        </header>

        {/* Avertissement documentaire */}
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 mb-8 text-xs text-amber-200/90 leading-relaxed">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
          <p>Synthèse documentaire destinée aux professionnels de santé. Ne constitue pas un avis médical personnalisé ni un dispositif médical. Référez-vous aux sources officielles citées.</p>
        </div>

        {/* Points clés */}
        {(article.key_points || []).length > 0 && (
          <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-5 mb-8" data-testid="blog-key-points">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-indigo-200 mb-3 uppercase tracking-wide">
              <CheckCircle2 className="w-4 h-4" /> Points clés
            </h2>
            <ul className="space-y-2">
              {article.key_points.map((p, i) => (
                <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-slate-200">
                  <span className="text-indigo-400 mt-0.5 shrink-0">✓</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-8">
          {(article.sections || []).map((s, i) => (
            <section key={i} data-testid={`blog-section-${i}`}>
              <h2
                className="text-xl font-bold text-white mb-3"
                style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}
              >
                {s.heading}
              </h2>
              {renderContent(s.content)}
            </section>
          ))}
        </div>

        {/* FAQ */}
        {faqValid.length > 0 && (
          <section className="mt-10" data-testid="blog-faq">
            <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}>
              Questions fréquentes
            </h2>
            <div className="space-y-3">
              {faqValid.map((f, i) => (
                <details key={i} className="group rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-[15px] font-medium text-slate-100">
                    {f.question}
                    <ChevronRight className="w-4 h-4 text-slate-500 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <p className="mt-2.5 text-[14px] leading-[1.65] text-slate-300">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Sources */}
        {(article.sources || []).length > 0 && (
          <section className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="blog-sources">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-3 uppercase tracking-wide">
              <BookMarked className="w-4 h-4" /> Sources
            </h2>
            <ol className="space-y-1.5">
              {article.sources.map((src, i) => (
                <li key={i} className="text-xs leading-relaxed text-slate-400 break-words">{src}</li>
              ))}
            </ol>
          </section>
        )}

        {/* Capture lecteurs */}
        <NewsletterSignup source={article.slug} />

        {/* CTA vers l'outil */}
        <div className="mt-12 rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-600/15 to-slate-900/40 p-6 text-center" data-testid="blog-article-cta">
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}>
            Gagnez du temps en consultation
          </h2>
          <p className="text-sm text-slate-300 mb-5 max-w-lg mx-auto">
            Fiche diagnostic, cotation CCAM, scores cliniques et recherche médicale sourcée — l'assistant documentaire des médecins.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-6 py-3 transition-colors"
            data-testid="blog-article-cta-button"
          >
            Accéder à Hippocrate MG <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Articles liés */}
        {related.length > 0 && (
          <section className="mt-12" data-testid="blog-related">
            <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}>
              À lire aussi
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="group rounded-xl border border-slate-800 bg-slate-900/40 hover:border-indigo-500/50 p-4 transition-colors"
                >
                  <span className="text-[11px] text-indigo-300">{r.category_label}</span>
                  <p className="mt-1 text-sm font-medium text-slate-100 leading-snug group-hover:text-indigo-300 transition-colors line-clamp-3">
                    {r.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </BlogLayout>
  );
};

export default BlogArticlePage;
